'use client'
import { useState } from 'react'
import { useCreateFormSubmissionMutation } from '@/core/graphql/hooks'
import { AvailabilityMeetingPlace, FormSubmissionStatus, FormTemplate } from '@/core/graphql/types'
import DynamicForm, { DynamicFormProps, FormDataRecord } from '@/shared/ui/form/DynamicForm'
import { useToast } from '@/hooks/use-toast'
import { useBooking } from '../../model/bookingContext'

type FormSubmissionSectionProps = {
  template: DynamicFormProps['template']
  meetingLink: string
  patientId: string
}

export function FormSubmissionSection({ template, meetingLink, patientId }: FormSubmissionSectionProps) {
  const { toast } = useToast()
  const { state, dispatch } = useBooking()

  const [createFormSubmission] = useCreateFormSubmissionMutation()

  const isSecondOpinion = state.meetingPlace === AvailabilityMeetingPlace.SecondOpinion

  const [formData, setFormData] = useState<FormDataRecord>((state.formData || {}) as FormDataRecord)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFormSubmit = async (data: FormDataRecord) => {
    setIsSubmitting(true)
    try {
      // Store form data in context
      dispatch({
        type: 'UPDATE_FORM_DATA',
        payload: data,
      })

      const result = await createFormSubmission({
        variables: {
          payload: {
            template: template._id,
            responses: Object.entries(data).map(([fieldId, value]) => ({
              fieldId,
              value,
            })),
            status: FormSubmissionStatus.Submitted,
            patient: patientId, // Include the patient ID in the payload
          },
        },
      })

      if (result.data?.createFormSubmission?._id) {
        console.log('Form submission successful:', result.data.createFormSubmission._id)

        // Store form submission ID in context
        dispatch({
          type: 'SET_FORM_SUBMISSION',
          payload: {
            id: result.data.createFormSubmission._id,
            data: data,
          },
          goToNextStep: !isSecondOpinion || !!state.startDate,
        })

        // For second opinions, we need to set a default start date
        if (isSecondOpinion && !state.startDate) {
          dispatch({
            type: 'SET_START_DATE',
            payload: new Date().toISOString(),
            goToNextStep: true,
          })
        }
      }
    } catch (error: unknown) {
      console.error('Error submitting form:', error)

      const apolloError = error as {
        message?: string
        graphQLErrors?: Array<{ message: string; extensions?: Record<string, unknown> }>
        networkError?: { result?: { errors?: Array<{ message: string }> } }
      }

      toast({
        variant: 'destructive',
        title: 'Error submitting form',
        description: apolloError.message || 'Something went wrong while submitting the form.',
      })

      // Log more detailed error information
      if (apolloError.graphQLErrors) {
        apolloError.graphQLErrors.forEach((graphQLError) => {
          console.error('GraphQL error:', graphQLError.message, graphQLError.extensions)
        })
      }

      if (apolloError.networkError) {
        console.error('Network error:', apolloError.networkError)
      }

      // Set a more specific error message if available
      const errorMessage =
        apolloError.graphQLErrors?.[0]?.message || apolloError.networkError?.result?.errors?.[0]?.message || 'Failed to submit form. Please try again.'

      setValidationErrors({ general: errorMessage })

      // Set error in context
      dispatch({
        type: 'SET_ERROR',
        payload: { field: 'formSubmission', message: errorMessage },
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      {validationErrors.general && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{validationErrors.general}</p>
        </div>
      )}
      <DynamicForm
        template={template}
        formData={formData}
        validationErrors={validationErrors}
        isSubmitting={isSubmitting}
        onFormChange={(data) => setFormData(data)}
        onSubmit={handleFormSubmit}
      />
    </div>
  )
}
